import React, { useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import SettingsBackArrow from '@/components/userSettings/SettingsBackArrow';
import useIsAdmin from '@/hooks/auth/useIsAdmin';
import useCheckAppVersion from '@/hooks/nativeApp/useCheckAppVersion';
import useAppDetails from '@/hooks/utilities/useAppDetails';
import useListenFromRn from '@/hooks/utilities/useListenFromRn';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import useAppSettings, { AppConfigKeys, MoonpayMappings, TAllowedCurrencies } from '@/hooks/admin/useAppSettings';
import { useIsMobile } from '@/hooks/utilities/use-mobile';
import Seo from '@/components/shared/Seo';

// type AppConfig = {
//   blockAppNotifs: boolean;
//   blockAppVersion: boolean;
//   paymentProvider: 'moonpay' | 'coinbase';
//   appVersion?: string;
//   alertMessage?: string;
// };

export default function AdminSettingsPage() {
  const {
    isAdmin,
    isLoading
  } = useIsAdmin();
  const {
    blockApp,
    blockAppVersion,
    isAppleNativeApp
  } = useAppDetails();
  const {
    appVersion
  } = useCheckAppVersion();
  const {
    sendMessageToRN,
    useRNHandler
  } = useListenFromRn();
  const [messagesFromRN, setMessagesFromRN] = useState<string[]>([]);
  const {
    appConfig,
    updateAppConfig
  } = useAppSettings();
  const [ip, setIP] = useState('');
  const [_, setLoading] = useState(true);
  const [__, setError] = useState(null);
  useEffect(() => {
    const getIP = async () => {
      try {
        const response = await fetch('/api/admin/get-local-ip');
        if (!response.ok) {
          throw new Error('Failed to fetch IP address');
        }
        const data = await response.json();
        setIP(data?.ip);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    getIP();
  }, []);
  useRNHandler('BUILD_VERSION', payload => {
    try {
      payload = JSON.parse(payload);
    } catch (_) {
      //
    }
    const message = 'BUILD_VERSION: ' + JSON.stringify(payload);
    if (payload) {
      setMessagesFromRN([...messagesFromRN, message]);
    }
  });
  useRNHandler('BLOCK_APP', payload => {
    try {
      payload = JSON.parse(payload);
    } catch (_) {
      //
    }
    const message = 'BLOCK_APP: ' + JSON.stringify(payload);
    if (payload) {
      setMessagesFromRN([...messagesFromRN, message]);
    }
  });
  useRNHandler('IN_APP_REVIEW_FAIL', payload => {
    try {
      payload = JSON.parse(payload);
    } catch (_) {
      //
    }
    const message = 'IN_APP_REVIEW_FAIL: ' + JSON.stringify(payload);
    if (payload) {
      setMessagesFromRN([...messagesFromRN, message]);
    }
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();
  if (isLoading) {
    return <div>Loading...</div>;
  }
  if (!isAdmin) {
    return <div className='w-full m-4'>
        <div className='flex items-center'>
          <SettingsBackArrow />
        </div>
        <div>You are not admin</div>
      </div>;
  }
  return <div className='w-full m-4 h-fit pb-[100px]' data-sentry-component="AdminSettingsPage" data-sentry-source-file="settings.tsx">
      <Seo title='Admin - Settings' data-sentry-element="Seo" data-sentry-source-file="settings.tsx" />
      <div className='flex items-center mb-4'>
        <SettingsBackArrow data-sentry-element="SettingsBackArrow" data-sentry-source-file="settings.tsx" />
      </div>
      {isMobile && <>
          <div className='w-full px-4'>Mobile Admin Settings</div>
          <div className='text-sm px-4 mt-4'>
            <div>App Id: {appVersion?.id}</div>
            <div>Build Number: {appVersion?.buildNumber}</div>
            <div>App Name: {appVersion?.name}</div>
            <div>App Version: {appVersion?.version}</div>
            <div>Block App (Notifs): {blockApp ? 'true' : 'false'}</div>
            <div>Block App (Version): {blockAppVersion ? 'true' : 'false'}</div>
            <div className='flex flex-col mt-2 w-full gap-y-2'>
              <Button className='w-64' onClick={() => {
            sendMessageToRN({
              action: 'ACTION_OPEN_APP_STORE',
              payload: {}
            });
          }}>
                Trigger Open App Store For Update
              </Button>
              <Button className='w-64' onClick={() => {
            if (isAppleNativeApp) {
              window.open('https://apps.apple.com/us/app/genius-wallet/id6738291878');
            } else {
              window.open('https://play.google.com/store/apps/details?id=com.tradegenius.xnative');
            }
          }}>
                Trigger Open App Store For Update (Link)
              </Button>
              <Button className='w-64' onClick={() => {
            sendMessageToRN({
              action: 'ACTION_TRIGGER_IN_APP_REVIEW',
              payload: {}
            });
          }}>
                Trigger In-App Review
              </Button>
              <Button className='w-64' onClick={() => {
            sendMessageToRN({
              action: 'ACTION_TRIGGER_APP_REVIEW',
              payload: {}
            });
          }}>
                Trigger Out Of App Review
              </Button>
              Set App To Local IP: {ip}
              <div className='flex flex-wrap'>
                <Button className='mx-1' onClick={() => {
              sendMessageToRN({
                action: 'SET_WEBVIEW_URL',
                payload: {
                  mode: 'prod'
                }
              });
            }}>
                  Prod
                </Button>
                <Button className='mx-1' onClick={() => {
              sendMessageToRN({
                action: 'SET_WEBVIEW_URL',
                payload: {
                  mode: 'staging'
                }
              });
            }}>
                  Staging
                </Button>
                <Button className='mx-1' onClick={() => {
              sendMessageToRN({
                action: 'SET_WEBVIEW_URL',
                payload: {
                  mode: 'dev'
                }
              });
            }}>
                  Dev
                </Button>
                <Button className='mx-1' onClick={() => {
              sendMessageToRN({
                action: 'SET_WEBVIEW_URL',
                payload: {
                  mode: 'local',
                  ip
                }
              });
            }}>
                  Local
                </Button>
              </div>
            </div>
            <div className='mt-2 flex flex-col gap-y-2'>
              Messages From RN:
              {messagesFromRN.map((msg, index) => {
            return <div className='flex flex-wrap' key={index}>
                    {index + 1}. {msg}
                  </div>;
          })}
            </div>
          </div>
        </>}

      <div className='px-4'>
        <div className='w-full mt-4'>App Admin Settings</div>

        <div className='w-full lg:w-1/2 pr-4 lg:pl-0 text-sm '>
          <div className='flex items-center justify-between pr-8 w-full mt-8 '>
            Block Mobile App For Notifications{' '}
            <Switch checked={appConfig?.blockAppNotifs} onClick={() => {
            updateAppConfig({
              blockAppNotifs: !appConfig?.blockAppNotifs
            });
          }} className='ml-4' data-sentry-element="Switch" data-sentry-source-file="settings.tsx" />
          </div>
          <div className='flex items-center justify-between pr-8 w-full mt-2 '>
            Block Mobile App For Version{' '}
            <Switch checked={appConfig?.blockAppVersion} onClick={() => {
            updateAppConfig({
              blockAppVersion: !appConfig?.blockAppVersion
            });
          }} className='ml-4' data-sentry-element="Switch" data-sentry-source-file="settings.tsx" />
          </div>
          <div className='flex items-center justify-between pr-8 w-full mt-2 '>
            Use Moonpay As Default (Currently Using {appConfig?.paymentProvider}){' '}
            <Switch checked={appConfig?.paymentProvider == 'moonpay'} onClick={() => {
            if (appConfig?.paymentProvider == 'moonpay') {
              updateAppConfig({
                paymentProvider: 'coinbase'
              });
            } else {
              updateAppConfig({
                paymentProvider: 'moonpay'
              });
            }
          }} className='ml-4' data-sentry-element="Switch" data-sentry-source-file="settings.tsx" />
          </div>
          <div className='flex flex-col  items-start justify-start pr-8 w-full mt-2 '>
            Change Onboarding Currency (Currently Using{' '}
            {appConfig?.[AppConfigKeys.onboardingCurrency]}){' '}
            <div className='flex items-center flex-wrap my-4 gap-x-2'>
              {Object.keys(MoonpayMappings).map((x: TAllowedCurrencies, index: number) => {
              return <Button key={index} onClick={() => {
                updateAppConfig({
                  [AppConfigKeys.onboardingCurrency]: x
                });
              }} variant={appConfig?.[AppConfigKeys.onboardingCurrency] == x ? 'pink' : 'blue'}>
                    {x}
                  </Button>;
            })}
            </div>
          </div>
          <div className='text-sm mt-4'>Update App Version</div>
          Current: {appConfig?.appVersion}
          <div className='w-full lg:w-1/2 relative '>
            <Input ref={inputRef} className='mt-4 h-12 text-[16px]' placeholder='Update App Version' data-sentry-element="Input" data-sentry-source-file="settings.tsx" />
            <Button onClick={() => {
            updateAppConfig({
              appVersion: inputRef.current?.value
            });
            inputRef.current.value = '';
          }} className='absolute right-0 top-1' data-sentry-element="Button" data-sentry-source-file="settings.tsx">
              Update
            </Button>
          </div>
          <div className='text-sm mt-4'>Add Alert Message (Posted Right Under Search)</div>
          Current: {appConfig?.alertMessage}
          <div className='relative w-full'>
            <Textarea ref={textAreaRef} placeholder='Update Alert Message' className='mt-4 min-h-32 ' data-sentry-element="Textarea" data-sentry-source-file="settings.tsx" />
            <Button onClick={() => {
            updateAppConfig({
              alertMessage: textAreaRef.current?.value
            });
            textAreaRef.current.value = '';
          }} className='absolute right-0 bottom-1' data-sentry-element="Button" data-sentry-source-file="settings.tsx">
              Update
            </Button>
          </div>
        </div>
      </div>
    </div>;
}